import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import { format, addDays, compareAsc } from "date-fns";
import toast from 'react-hot-toast';
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography,  
  CircularProgress, 
  IconButton, 
  Tooltip, 
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchLegMembersByFlt } from 'state/api';
import { filterFlights } from 'state/helpers';
import { logClientAction } from 'state/logger';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import CrewListDialog from 'components/CrewListDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import DeadheadingList from "./DeadheadingList";


const Deadheadings = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode);
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  

  const { startDate, endDate } = useDateRangeContext();
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);

  const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
  const [crewList, setCrewList] = useState([]);
  const [jumpList, setJumpList] = useState([]);
  const [flight, setFlight] = useState([]);
  
  const [deadheadings, setDeadheadings] = useState([]);
  
  
  useEffect(() => {    
    const handleDeadheadings = async () => {
      if (!startDate || !endDate) return null; 
      
      try {
        setLoading(true);

        const url = '/legmembers/deadheadings';
        const payload = { 
          startDate: addDays(startDate, -1), 
          endDate: addDays(endDate, 1), 
        };          

        const res = await api.post(url, payload);
        
        if (res.data?.length > 0) {        
          const filtered = res.data.filter(f => {
            const flightDateToCompare = timeMode === 'UTC' 
              ? f.flightDate 
              : f.flightDateLocalBase;
        
            return (
              compareAsc(flightDateToCompare, startDate) >= 0 &&
              compareAsc(flightDateToCompare, endDate) <= 0
            );
          });

          setDeadheadings(filtered);
        }
      } catch (error) {
        setError(error)
        toast.error(error?.message || "Failed to fetch crew routes!");
      } finally {
        setLoading(false);
      }
    };
    
    handleDeadheadings();
  }, [timeMode, startDate, endDate]); 

  const filteredFlights = useMemo(() => {
    const flights = filterFlights({ flights: deadheadings, filters, timeMode });
    return flights;
  }, [filters, timeMode, deadheadings]);
  
  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);

  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };
    
  const handleCloseCrewListDialog = () => {
    setOpenCrewListDialog(false);
    setCrewList([]);
  };

  const handleViewClick = useCallback(async (param) => {
    try {      
      const {
        flightDate, 
        flightDesc, 
        dep,
      } = param;

      const {
        flightDetails, 
        legMembers, 
        jumpSeaters
      } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);
      
      setFlight(flightDetails);
      setCrewList(legMembers);
      setJumpList(jumpSeaters);
      setOpenCrewListDialog(true);
    } catch (error) {
      setOpenCrewListDialog(false);
    } 
  }, []);

  const handleExcelJSDownload = () => {
    const exportToExcel = async (data, fileName = "DeadheadingList.xlsx") => {
      try {
        // 1. Create a new workbook
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("DeadheadingList");

        // Add title row
        worksheet.addRow(["Deadheading List"]);
        worksheet.mergeCells("A1:K1");
        worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
        worksheet.getCell("A1").font = { size: 16, bold: true };

        // Add subtitle row
        const fullName = user?.firstName && user?.lastName ? user?.lastName + ' ' + user?.firstName : 'Unknown';
        const createdDate = `Reported by: ${fullName}, at: ${new Date().toLocaleString()}`;
        worksheet.addRow([createdDate]);
        worksheet.mergeCells("A2:K2");
        worksheet.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
        worksheet.getCell("A2").font = { italic: true };

        // Add subtitle row
        const timingMode = `Time mode: ${timeMode}`;
        worksheet.addRow([timingMode]);
        worksheet.mergeCells("A3:K3");
        worksheet.getCell("A3").alignment = { horizontal: "center", vertical: "middle" };
        worksheet.getCell("A3").font = { italic: true };

        // Add an empty row
        worksheet.addRow();
        
        // 2. Define column headers
        const columns = [
          { header: "No", key: "No", width: 6 },
          { header: "Flight Date", key: "FlightDate", width: 15 },
          { header: "Flight No", key: "FlightNo", width: 10 },
          { header: "Sector", key: "Sector", width: 10 },
          { header: "ACType", key: "ACType", width: 10 },
          { header: "FlightReg", key: "FlightReg", width: 10 },
          { header: "Etd", key: "Etd", width: 8 },
          { header: "Eta", key: "Eta", width: 8 },
          { header: "Atd", key: "Atd", width: 8 },
          { header: "Ata", key: "Ata", width: 8 },
          { header: "Crew List", key: "CrewList", width: 40 },
        ];

        worksheet.columns = columns;
        // Add headers
        worksheet.addRow(columns?.map((col) => col.header));

        // 3. Add data rows
        data.forEach((row) => {
          worksheet.addRow(row);
        });

        // 4. Apply styles (e.g., wrap text for certain columns)
        worksheet.eachRow((row, rowIndex) => {
          row.eachCell((cell) => {
            // Apply alignment and text wrapping
            cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
            
            // Apply border styles
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
            
            if (rowIndex === 4) {
              // Style for headers
              cell.font = { bold: true };
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFCC" },
              };
            }
          });
        });
        
        worksheet.getCell("A1").value = "Deadheading List";

        // 5. Generate and save the file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName); 
      } catch (error) {
        toast.error(error || "Failed to export to Excel.");
      }
    };

    if (!filteredFlights  || filteredFlights.length === 0) {
      toast.warning("No data available to export.");
      return;
    }
            
    const transformedFlights = filteredFlights.map((flight, index) => {
      const crewList = flight?.crewMembers        
        .map(member => `${member.crewCode} ${member.crewName} (${member.post} | ${member.base})`) 
        .join("\n");

      return {
        No: index + 1,
        FlightDate: timeMode === "UTC" ? format(flight.flightDate, "yyyy-MM-dd") : format(flight.flightDateLocalBase, "yyyy-MM-dd"),
        FlightNo: flight.flightNo || '',
        Sector: flight?.sector || '',
        ACType: flight?.acType || '',
        FlightReg: flight?.flightReg || '',
        Etd: flight?.etd || '',
        Eta: flight?.eta || '',
        Atd: flight?.atd || '',
        Ata: flight?.ata || '',
        CrewList: crewList || '',
      };
    });

    // Add a timestamp to the filename
    const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
    const fileName = `DeadheadingList_${timestamp}.xlsx`;

    exportToExcel(transformedFlights, fileName);
  };

  const acTypeOptions = [...new Set(deadheadings?.map(f => f.acType))].sort();
  const flightRegOptions = [...new Set(deadheadings?.map(f => f.flightReg))].sort();
  const sectorTypeOptions = [...new Set(deadheadings?.map(f => f.sectorType))].sort();

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      headerClassName: 'theme-column--header',
      filterable: false,      
      sortable: false,
      ...isMobile ? { width: 60 } : { flex: 1 },
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
      headerName: "Date",
      headerClassName: 'theme-column--header',
      pinned: 'left',
      ...isMobile ? { width: 100 } : { flex: 1.5 },
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yy")}`;
      },
    },        
    {
      field: "flightNo",
      headerName: "FlightNo",
      pinned: 'left',
      ...isMobile ? { width: 85 } : { flex: 1.2 },
    },        
    {
      field: "sector",
      headerName: "Sector",
      ...isMobile ? { width: 110 } : { flex: 1.5 },
    },
    {
      field: "acType",
      headerName: "ACType",
      ...isMobile ? { width: 100 } : { flex: 1.3 },
    },
    {
      field: "flightReg",
      headerName: "Reg",
      ...isMobile ? { width: 110 } : { flex: 1.5 },
    },        
    {
      field: timeMode === 'UTC' ? "etd" : "etdLocalBase",
      headerName: "Etd",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: timeMode === 'UTC' ? "eta" : "etaLocalBase",
      headerName: "Eta",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },        
    {
      field: timeMode === 'UTC' ? "atd" : "atdLocalBase",
      headerName: "Atd",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },    
    {
      field: timeMode === 'UTC' ? "ata" : "ataLocalBase",
      headerName: "Ata",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: 'crewMembers',
      headerName: 'CrewMembers',
      ...isMobile ? { width: 330 } : { flex: 6 },
      renderCell: (params) => {   
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {params.row.crewMembers?.map((member, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                alignContent='flex-start'
                gap={1}
                sx={{
                  color: member.gender === 'FM' 
                    ? theme.palette.secondary.main 
                    : theme.palette.primary.dark,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: '30px',
                  }}
                >
                  {member.crewCode}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {member.crewName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  ({member.post} | {member.base})
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
  ], [timeMode, theme, isMobile]);

  const hiddenColumns = useMemo(() => {
    return {
      _id: false,
      acType: false,
      flightReg: false,
      doorClose: false,
      atd: false,
      ata: false,
      atdLocalBase: false,
      ataLocalBase: false,
    }
  }, []);

  const nonMobileHiddenColumns = useMemo(() => {
    return {
      _id: false,
    }
  }, []);

  const actionColumn = useMemo(() => [{
    field: "action",
    headerName: "Action",
    width: 80,
    filterable: false,      
    sortable: false,    
    renderCell: (params) => (
      <FlexCenter
        height="100%" 
        gap={1}
      >
        <Tooltip title="View">
          <IconButton
            aria-label="View"
            sx={{
              color: theme.palette.primary.dark,
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.green.deep,
              }
            }}
            onClick={() => {
              handleViewClick(params.row)}
            }
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>          
      </FlexCenter>
    ),
  }], [theme, handleViewClick]); 

  const columnsToShow = user.role === 'viewer' ? columns : [...columns, ...actionColumn];
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);

  const totalDeadheadings = filteredFlights.reduce((acc, f) => 
    acc + (f.crewMembers?.length || 0),
    0
  );


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Deadheadings" 
          subtitle={loading ? "Loading..." : `List of ${totalDeadheadings || 0} Bookings`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Download Excel file" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                  handleExcelJSDownload();
                  logClientAction("EXPORT_REPORT", { fileName: "Deadheadings", format: "Excel", startDate, endDate });
                }}
                >
                  <DownloadIcon />
                </StyledButton>
              </span>  
            </Tooltip>            
            <FilterButton
              filters={filters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}          
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">
            Error: {error?.message || ''}
          </Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box 
            ref={scrollRef}
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <DeadheadingList 
                deadheadings={filteredFlights}                                
                onClick={handleViewClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredFlights}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen &&
        <FlightFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          acTypeOptions={acTypeOptions} 
          flightRegOptions={flightRegOptions}
          sectorTypeOptions={sectorTypeOptions}
          currentFilters={filters}
        />
      }
      
      {openCrewListDialog &&
        <CrewListDialog 
          open={openCrewListDialog}
          onClose={handleCloseCrewListDialog}
          flight={flight}
          crewList={crewList}
          jumpList={jumpList}
        />
      }
    </Box>
  );
};

export default Deadheadings;
